/**
 * E5.4 step 1 (ARCHITECTURE §5.17; lane F): the planet's forest as a pure function of the planet's seed and a cell. Every
 * cell's stands and trees come from its own seeded streams, so any one cell can be made alone, in any order — by the
 * sim's held band (step 4) and the renderer's far field (step 2) alike — and the same tree always has the same id.
 *
 * - **Cells**: core/cells.ts's cube-sphere at `forestCellsPerEdge` (about FOREST.cellPeople across).
 * - **The biome field** (decision 151: a planet's own kinds, concentrated by place): each of the planet's biomes is a
 *   smooth noise over the sphere about FOREST.regionPeople across, weighted by `biomesFor`; the strongest wins where it
 *   clears FOREST.openBelow, else the ground is open. A pure function of the seed and the direction.
 * - **Stands**: a cell draws FOREST.anchorsPerCell candidate anchors (stream `…|a`). A candidate is a stand where the
 *   field grows a biome, it roots (STAND_ANCHOR_MAX_SLOPE), it is clear of the caller's keep-out, and no candidate that
 *   outranks it (a hash of its cell and slot) and stands within the two radii and a gap is a stand — a greedy independent
 *   set, packed as dressBattleSite packs stands. It reads only the candidates round it, whatever order cells are asked in.
 * - **Trees**: each stand laid out as `dressBattleSite` lays one (its biome's shapes, count, spacing; stream `…|<k>`),
 *   every draw made before its slope test so a tree on a cliff leaves the others where they were; a tree crowding one of
 *   an outranking stand gives way. Id = FOREST.idBase + cell × FOREST.perCell + k × 64 + i — stable whatever drops.
 * - **Measured** (three Lab planets, Oct 11): 43–73 % of the ground in woods; a sonora megaplanet ~110,000 trees in all
 *   (0.004 a square person; the woods at about a third of the Lab site's packing), a rough tephra ~20,000 (its anchors
 *   too steep to root); 0.02–0.09 ms a cell.
 *
 * The terrain is read as passed (slopes, a biome's low or high ground): the caller decides which planet that is.
 * Presentation and sim read the same function. dmath only (through biomes.ts's helpers); no shared stream, no camera.
 */
import type { BiomeId, FloraState, GameState, PlanetState, Vec3 } from '../types';
import { SeededRandom } from './random';
import { typedArrayHash } from './contentHash';
import { dasin } from './dmath';
import { cellOf, cellPoint, cellsNear } from './cells';
import { getSurfaceRadius } from './surface';
import { reliefWoods, standChance, RELIEF_WOODS, type ForestDensityMode, type ReliefWoods } from './forestDensity';
import {
  BIOMES, BIOME_MAX_SLOPE, DISC_ONLY, GROUND_PICKS, STAND_ANCHOR_MAX_SLOPE, biomesFor, contourHeading, layoutStand, offsetOnSphere,
  pick, slopeAt, standReach, tangentBasis,
} from './biomes';

export const FOREST = {
  /** A cell's side at a face's centre, in person-heights. */
  cellPeople: 40,
  /** Candidate stand anchors a cell draws (the stands' gap thins them to what fits: a greedy independent set). */
  anchorsPerCell: 8,
  /** The biome field's breadth: woods of one kind about this wide (person-heights). */
  regionPeople: 260,
  /** Where no biome's field reaches this, the ground is open (no stand). */
  openBelow: 0.62,
  /** Clear ground between two stands' rims (person-heights; dressBattleSite's `fits`). */
  standGapPeople: 4,
  /** Forest ids: idBase + cell × perCell + slot, inside 32 bits (propHash), above every dressed and stress id. */
  idBase: 134217728,   // 2^27
  perCell: 512,
} as const;

/** A forest tree's id: its cell, its stand's candidate (< 8) and its place in the stand's layout (< 64). */
export function forestTreeId(cell: number, k: number, i: number): number {
  return FOREST.idBase + cell * FOREST.perCell + k * 64 + i;
}
/** The cell and slot of a forest id (null: not a forest tree). */
export function forestIdCell(id: number): number | null {
  return id >= FOREST.idBase ? Math.floor((id - FOREST.idBase) / FOREST.perCell) : null;
}

/**
 * Decision 158 (the owner: "the planets"): a planet's forest seed — its archetype and a hash of its generated mesh
 * (`basePositions`, which only the generator writes: a crater changes the heights, not these). The same in every battle
 * on the planet (the sector carries its planets, a save regenerates them alike), so the same wood stands there and a
 * felled tree's id (forestTreeId) names the same tree in the next battle.
 */
export function planetForestSeed(planet: Pick<PlanetState, 'archetype' | 'basePositions'>): string {
  return `${planet.archetype}|${typedArrayHash(planet.basePositions).full.toString(36)}`;
}

/** The forest's cells per face edge on a planet (`person`: a person's height, world units). */
export function forestCellsPerEdge(planet: Pick<PlanetState, 'radius'>, person: number): number {
  return Math.max(8, Math.round(((Math.PI / 2) * planet.radius) / (FOREST.cellPeople * person)));
}

// ---- the biome field ----------------------------------------------------------------------------------------------

/** A 32-bit hash of a string (FNV-1a). */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

/** A lattice point's value in [0, 1). */
function lattice(x: number, y: number, z: number, seed: number): number {
  let h = Math.imul(x, 0x8da6b343) ^ Math.imul(y, 0xd8163841) ^ Math.imul(z, 0xcb1ab31f) ^ seed;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Smooth 3-D value noise in [0, 1) (trilinear over a smoothstep): arithmetic only, the same on every engine. */
function valueNoise(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = x - ix, fy = y - iy, fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  const c = (dx: number, dy: number, dz: number) => lattice(ix + dx, iy + dy, iz + dz, seed);
  const x00 = c(0, 0, 0) + (c(1, 0, 0) - c(0, 0, 0)) * sx, x10 = c(0, 1, 0) + (c(1, 1, 0) - c(0, 1, 0)) * sx;
  const x01 = c(0, 0, 1) + (c(1, 0, 1) - c(0, 0, 1)) * sx, x11 = c(0, 1, 1) + (c(1, 1, 1) - c(0, 1, 1)) * sx;
  const y0 = x00 + (x10 - x00) * sy, y1 = x01 + (x11 - x01) * sy;
  return y0 + (y1 - y0) * sz;
}

interface Field { channels: Array<{ biome: BiomeId; weight: number; seed: number }>; scale: number }
const fieldMemo = new WeakMap<object, Map<string, Field>>();

function fieldOf(planet: Pick<PlanetState, 'radius' | 'archetype' | 'palette'>, planetSeed: string, person: number): Field {
  let bySeed = fieldMemo.get(planet);
  if (!bySeed) { bySeed = new Map(); fieldMemo.set(planet, bySeed); }
  const key = `${planetSeed}|${person}`;
  let f = bySeed.get(key);
  if (!f) {
    const list = biomesFor(planet), top = Math.max(1e-9, ...list.map(([, w]) => w));
    f = { channels: list.map(([biome, w]) => ({ biome, weight: w / top, seed: hashString(`${planetSeed}|biome|${biome}`) })), scale: planet.radius / (FOREST.regionPeople * person) };
    bySeed.set(key, f);
  }
  return f;
}

/** The biome the field grows at a unit direction, or null: open ground. */
export function forestBiomeAt(planet: Pick<PlanetState, 'radius' | 'archetype' | 'palette'>, planetSeed: string, person: number, d: Vec3): BiomeId | null {
  const f = fieldOf(planet, planetSeed, person);
  let best: BiomeId | null = null, bestScore: number = FOREST.openBelow;
  for (const ch of f.channels) {
    // a weaker biome needs a stronger field to win (its weight scales the field's swing above the open line)
    const v = valueNoise(d.x * f.scale, d.y * f.scale, d.z * f.scale, ch.seed);
    const score = FOREST.openBelow + (v - FOREST.openBelow) * ch.weight;
    if (score > bestScore) { bestScore = score; best = ch.biome; }
  }
  return best;
}

// ---- the jungle (67.61) --------------------------------------------------------------------------------------------

/**
 * 67.61 (the owner, Oct 19: *"I do want to be able to have density of forests that is jungle like, not everywhere but just
 * dialing up forest density"*): a region field over the wooded biomes — value noise JUNGLE_WOODS.regionPeople across, keyed
 * on the planet's seed — whose top `share × the biome's weight` is jungle. A stand anchored in it is a jungle stand: its
 * trunks × trunkMul (lane F's cap, about 2× today's packing, never stacked on the relief's dense core), tighter (spacing ×
 * spacingMul) — its draws the same and in the same order, so the trunks are a keyed pick inside the cell (E5.4's
 * invariant). Never the open woodlands (heath, savanna, scrub, the barrens, the burn). Share 0 (GameConfig.forestJungle
 * absent): none, and nothing else moves.
 */
export const JUNGLE_WOODS = {
  regionPeople: 320,
  biomes: { broadleaf: 1, lanternGrove: 1, hollowWood: 0.8, birchGlade: 0.5, firStand: 0.35 } as Partial<Record<BiomeId, number>>,
  trunkMul: 2,
  spacingMul: 0.72,
};
const jungleSeeds = new Map<string, number>();
/** Whether a stand of `biome` anchored at `d` is jungle, at the planet's jungle `share` (0: never). */
export function jungleAt(planet: Pick<PlanetState, 'radius'>, planetSeed: string, person: number, d: Vec3, biome: BiomeId | null, share: number): boolean {
  if (!(share > 0) || !biome) return false;
  const w = JUNGLE_WOODS.biomes[biome] ?? 0;
  if (w <= 0) return false;
  let seed = jungleSeeds.get(planetSeed);
  if (seed === undefined) { seed = hashString(`${planetSeed}|jungle`); jungleSeeds.set(planetSeed, seed); }
  const k = planet.radius / (JUNGLE_WOODS.regionPeople * person);
  const v = valueNoise(d.x * k, d.y * k, d.z * k, seed);
  // (the smoothed noise bunches round a half: stretched, so a share reads as about that much of the ground)
  return Math.min(1, Math.max(0, (v - 0.5) * 2.1 + 0.5)) > 1 - Math.min(1, share * w);
}
/** A stand's spacing: its biome's, × JUNGLE_WOODS.spacingMul when it is jungle. */
function standSpacing(planet: PlanetState, planetSeed: string, person: number, c: ForestCandidate, m: StandMemo): number {
  const base = BIOMES[c.biome!].spacing;
  return m.jungle > 0 && jungleAt(planet, planetSeed, person, c.dir, c.biome, m.jungle) ? base * JUNGLE_WOODS.spacingMul : base;
}

// ---- stands --------------------------------------------------------------------------------------------------------

/** A stand's candidate anchor: where it is, and the biome the field grows there (null: open ground, no stand). */
export interface ForestCandidate { cell: number; k: number; dir: Vec3; biome: BiomeId | null }

const candidateMemo = new WeakMap<object, Map<string, ForestCandidate[]>>();

/**
 * A cell's candidate anchors: FOREST.anchorsPerCell points in the cell from its own stream; a biome that seeks low or
 * high ground takes the lowest or highest of GROUND_PICKS draws in its own biome (dressBattleSite's ground pick).
 */
export function forestCandidates(planet: PlanetState, planetSeed: string, person: number, n: number, cell: number): ForestCandidate[] {
  let byKey = candidateMemo.get(planet);
  if (!byKey) { byKey = new Map(); candidateMemo.set(planet, byKey); }
  const key = `${planetSeed}|${person}|${n}|${cell}`;
  const kept = byKey.get(key);
  if (kept) return kept;
  const rng = new SeededRandom(`${planetSeed}|forest|${cell}|a`);
  const out: ForestCandidate[] = [];
  for (let k = 0; k < FOREST.anchorsPerCell; k++) {
    const draws: Vec3[] = [];
    for (let g = 0; g < GROUND_PICKS; g++) draws.push(cellPoint(cell, n, rng.range(0, 1), rng.range(0, 1)));
    const biome = forestBiomeAt(planet, planetSeed, person, draws[0]);
    let dir = draws[0];
    const pref = biome ? BIOMES[biome].ground ?? 'any' : 'any';
    if (pref !== 'any') {
      let bestH = getSurfaceRadius(planet, dir);
      for (let g = 1; g < GROUND_PICKS; g++) {
        if (forestBiomeAt(planet, planetSeed, person, draws[g]) !== biome) continue;
        const h = getSurfaceRadius(planet, draws[g]);
        if (pref === 'low' ? h < bestH : h > bestH) { dir = draws[g]; bestH = h; }
      }
    }
    out.push({ cell, k, dir, biome });
  }
  byKey.set(key, out);
  return out;
}

/** A disc on the ground the forest keeps out of (the battle's own dressed area, a base): a direction and an angle. */
export interface ForestKeepOut { dir: Vec3; angle: number }

/** A candidate's rank: a hash of its cell and slot, so who yields to whom is spread evenly (no long chains). */
function rankOf(c: ForestCandidate, seed: number): number {
  return lattice(c.cell, c.k, 0x5f3759df, seed);
}

interface StandMemo { kept: Map<number, boolean>; trees: Map<number, FloraState[]>; seed: number; density: ForestDensityMode; relief: ReliefWoods | null; jungle: number }
const standMemo = new WeakMap<object, Map<string, StandMemo>>();
const NO_KEEP_OUT: readonly ForestKeepOut[] = [];
const keepOutIds = new WeakMap<readonly ForestKeepOut[], number>();
let keepOutNext = 1;

function memoFor(planet: PlanetState, planetSeed: string, person: number, n: number, keepOut: readonly ForestKeepOut[], density: ForestDensityMode = 'flat', windSeed = '', jungle = 0): StandMemo {
  let byKey = standMemo.get(planet);
  if (!byKey) { byKey = new Map(); standMemo.set(planet, byKey); }
  let kid = keepOutIds.get(keepOut);
  if (kid === undefined) { kid = keepOut.length ? keepOutNext++ : 0; keepOutIds.set(keepOut, kid); }
  const key = `${planetSeed}|${person}|${n}|${kid}${density === 'flat' ? '' : `|${density}|${windSeed}`}${jungle > 0 ? `|j${jungle}` : ''}`;
  let m = byKey.get(key);
  if (!m) { m = { kept: new Map(), trees: new Map(), seed: hashString(`${planetSeed}|rank`), density, relief: density === 'relief' ? reliefWoods(planet, windSeed, person) : null, jungle: jungle > 0 ? jungle : 0 }; byKey.set(key, m); }
  return m;
}

const slotOf = (c: ForestCandidate) => c.cell * 8 + c.k;
/** Whether q outranks c (a strict order: the hashed rank, then the slot). */
function outranks(q: ForestCandidate, c: ForestCandidate, seed: number): boolean {
  const rq = rankOf(q, seed), rc = rankOf(c, seed);
  return rq > rc || (rq === rc && slotOf(q) > slotOf(c));
}

/**
 * Whether a candidate is a stand: it grows a biome, roots, clears the keep-out, and no candidate that outranks it and
 * stands within the two stands' radii and the gap is a stand itself — a greedy independent set under a hashed rank:
 * local, deterministic, as close-packed as dressBattleSite's stands (its `fits`: radius + radius + 4 people; a scatter's
 * or a belt's outer trees may reach into a neighbour's, and give way tree by tree in forestCell). Memoised per planet
 * object, seed, grid and keep-out (a crater's new planet starts afresh).
 */
function isStand(planet: PlanetState, planetSeed: string, person: number, n: number, c: ForestCandidate, keepOut: readonly ForestKeepOut[], m: StandMemo): boolean {
  const key = slotOf(c);
  const known = m.kept.get(key);
  if (known !== undefined) return known;
  const P = person / planet.radius;
  let ok = !!c.biome && slopeAt(planet, c.dir, 2 * person) <= STAND_ANCHOR_MAX_SLOPE;
  const r = c.biome ? BIOMES[c.biome].radius * P : 0;
  if (ok) ok = !keepOut.some(o => angleBetween(c.dir, o.dir) < o.angle + r);
  // E5.4's density by relief (core/forestDensity.ts; off: 'flat'): the stand roots by its chance there — a draw of its own,
  // before the neighbours' test, so a candidate the ground thins blocks no other — and in the dense core packs closer
  let gap = FOREST.standGapPeople * P;
  if (ok && m.relief && c.biome) {
    const chance = standChance(planet, m.relief, c.dir, c.biome);
    ok = new SeededRandom(`${planetSeed}|dense|${c.cell}|${c.k}`).next() < chance;
    if (chance / Math.max(1e-9, RELIEF_WOODS.ceiling[c.biome] ?? 1) >= RELIEF_WOODS.denseFrom) gap *= RELIEF_WOODS.denseGap;
  }
  if (ok) {
    outer: for (const other of cellsNear(c.dir, r + gap + MAX_RADIUS_PEOPLE * P, n)) {
      for (const q of forestCandidates(planet, planetSeed, person, n, other)) {
        if (!q.biome || slotOf(q) === key || !outranks(q, c, m.seed)) continue;
        if (angleBetween(c.dir, q.dir) >= r + BIOMES[q.biome].radius * P + gap) continue;
        if (isStand(planet, planetSeed, person, n, q, keepOut, m)) { ok = false; break outer; }
      }
    }
  }
  m.kept.set(key, ok);
  return ok;
}

/** The stands of a cell: its candidates that are stands (see isStand), in slot order. */
export function forestStands(planet: PlanetState, planetSeed: string, person: number, n: number, cell: number, keepOut: readonly ForestKeepOut[] = NO_KEEP_OUT, density: ForestDensityMode = 'flat', windSeed = '', jungle = 0): ForestCandidate[] {
  const m = memoFor(planet, planetSeed, person, n, keepOut, density, windSeed, jungle);
  return forestCandidates(planet, planetSeed, person, n, cell).filter(c => isStand(planet, planetSeed, person, n, c, keepOut, m));
}

/** The angle between two unit directions (from the chord: dmath, the same on every engine). */
function angleBetween(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return 2 * dasin(Math.min(1, Math.sqrt(dx * dx + dy * dy + dz * dz) / 2));
}

/**
 * A stand's trees as laid out: its biome's shape, count and spacing (stream `…|<k>`), each tree's draws made before its
 * slope and keep-out tests, so a dropped tree moves no other. Before any neighbour gives way (see forestCell).
 */
function standTrees(planet: PlanetState, planetIndex: number, planetSeed: string, person: number, s: ForestCandidate, keepOut: readonly ForestKeepOut[], m: StandMemo): FloraState[] {
  const key = slotOf(s), kept = m.trees.get(key);
  if (kept) return kept;
  const P = person / planet.radius, def = BIOMES[s.biome!], out: FloraState[] = [];
  const rng = new SeededRandom(`${planetSeed}|forest|${s.cell}|${s.k}`);
  const shape = pick(def.shapes ?? DISC_ONLY, rng);
  const heading = shape === 'contour' ? contourHeading(planet, s.dir, 2 * person) : shape === 'belt' ? rng.range(0, Math.PI) : 0;
  const [t1, t2] = tangentBasis(s.dir);
  // (E5.4's density by relief: in the dense core more trees, tighter — the count drawn first, as ever, so the draws keep their order)
  const count = rng.int(def.stand[0], def.stand[1]);
  const dense = !!m.relief && standChance(planet, m.relief, s.dir, s.biome!) / Math.max(1e-9, RELIEF_WOODS.ceiling[s.biome!] ?? 1) >= RELIEF_WOODS.denseFrom;
  // (67.61: a jungle stand — trunks × trunkMul, tighter — in place of the dense core's, never on top of it)
  const jungle = m.jungle > 0 && jungleAt(planet, planetSeed, person, s.dir, s.biome, m.jungle);
  const layout = jungle
    ? layoutStand(shape, Math.min(64, Math.round(count * JUNGLE_WOODS.trunkMul)), def.radius, def.spacing * JUNGLE_WOODS.spacingMul, rng, heading)
    : layoutStand(shape, dense ? Math.min(64, Math.round(count * RELIEF_WOODS.denseTrees)) : count, def.radius, dense ? def.spacing * RELIEF_WOODS.denseSpacing : def.spacing, rng, heading);
  layout.forEach((o, i) => {
    const floraType = pick(def.trees, rng), scale = rng.range(0.85, 1.15);
    if (i >= 64) return;
    const d = offsetOnSphere(s.dir, t1, t2, o.x, o.y, P);
    if (slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) return;
    if (keepOut.some(k => angleBetween(d, k.dir) < k.angle)) return;
    out.push({ id: forestTreeId(s.cell, s.k, i), floraType, planetIndex, surfaceDir: d, alive: true, scale, size: 'normal', biome: s.biome! });
  });
  m.trees.set(key, out);
  return out;
}

/**
 * The trees of one cell on `planet` (index `planetIndex` in the state): its stands' trees, less any that crowds a tree
 * of an outranking stand (closer than 0.85 of the smaller spacing, as dressBattleSite's trees give way). Pure: the same
 * arguments give the same trees, in the same order, with the same ids.
 */
export function forestCell(planet: PlanetState, planetIndex: number, planetSeed: string, person: number, n: number, cell: number, keepOut: readonly ForestKeepOut[] = NO_KEEP_OUT, density: ForestDensityMode = 'flat', windSeed = '', jungle = 0): FloraState[] {
  const P = person / planet.radius, m = memoFor(planet, planetSeed, person, n, keepOut, density, windSeed, jungle), out: FloraState[] = [];
  for (const s of forestStands(planet, planetSeed, person, n, cell, keepOut, density, windSeed, jungle)) {
    const reach = forestStandReach(s.biome!) * P;
    // the stands that outrank this one and reach into it
    const rivals: Array<{ trees: FloraState[]; spacing: number }> = [];
    for (const other of cellsNear(s.dir, reach + MAX_REACH_PEOPLE * P, n)) {
      for (const q of forestCandidates(planet, planetSeed, person, n, other)) {
        if (!q.biome || slotOf(q) === slotOf(s) || !outranks(q, s, m.seed)) continue;
        if (angleBetween(s.dir, q.dir) >= reach + forestStandReach(q.biome) * P) continue;
        if (!isStand(planet, planetSeed, person, n, q, keepOut, m)) continue;
        rivals.push({ trees: standTrees(planet, planetIndex, planetSeed, person, q, keepOut, m), spacing: standSpacing(planet, planetSeed, person, q, m) });
      }
    }
    const mine = standSpacing(planet, planetSeed, person, s, m);
    for (const t of standTrees(planet, planetIndex, planetSeed, person, s, keepOut, m)) {
      const crowded = rivals.some(r => r.trees.some(u => angleBetween(t.surfaceDir, u.surfaceDir) < 0.85 * Math.min(mine, r.spacing) * P));
      if (!crowded) out.push({ ...t, surfaceDir: { ...t.surfaceDir } });
    }
  }
  return out;
}

/**
 * E5.4 step 4: the ground the forest is placed on — the planet with its heightmap at setup (`basis`), the same object
 * while it is (so the memos hold), whatever craters have done since: a cell made later is the cell made earlier.
 */
const basisMemo = new WeakMap<Float32Array, PlanetState>();
export function basisPlanet(planet: PlanetState, basis: Float32Array): PlanetState {
  let b = basisMemo.get(basis);
  if (!b) { b = { ...planet, heightmap: basis }; basisMemo.set(basis, b); }
  return b;
}

/**
 * E5.4: where the forest keeps out of on a planet, as at setup — the battle's own dressed areas (GameState.dressedAreas)
 * and every structure's ground (6 people round it, and a stand's radius and the gap past that). The drawing and the sim
 * read the same discs (core/forestHeld.ts keeps them in the state).
 */
export function forestKeepOutAt(state: Pick<GameState, 'planets' | 'structures'> & Partial<Pick<GameState, 'dressedAreas'>>, planetIndex: number, person: number): ForestKeepOut[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const P = person / planet.radius, margin = (Math.max(...Object.values(BIOMES).map(b => b.radius)) + FOREST.standGapPeople) * P;
  const discs: ForestKeepOut[] = (state.dressedAreas ?? []).filter(a => a.planetIndex === planetIndex).map(a => ({ dir: a.dir, angle: a.angle }));
  for (const st of state.structures ?? []) {
    if (st.planetIndex !== planetIndex) continue;
    const l = Math.sqrt(st.surfaceDir.x * st.surfaceDir.x + st.surfaceDir.y * st.surfaceDir.y + st.surfaceDir.z * st.surfaceDir.z) || 1;
    discs.push({ dir: { x: st.surfaceDir.x / l, y: st.surfaceDir.y / l, z: st.surfaceDir.z / l }, angle: 6 * P + margin });
  }
  return discs;
}

/** The forest cell a direction falls in. */
export function forestCellOf(d: Vec3, n: number): number { return cellOf(d.x, d.y, d.z, n); }

/** The widest stand radius and reach of any biome (person-heights): how far a search for rivals looks. */
const MAX_RADIUS_PEOPLE = Math.max(...Object.values(BIOMES).map(b => b.radius));
const MAX_REACH_PEOPLE = Math.max(...(Object.keys(BIOMES) as BiomeId[]).map(b => forestStandReach(b)));

/** A stand's reach in person-heights: its biome's radius × the widest of the shapes it takes (a belt, a scatter reach past it). */
export function forestStandReach(biome: BiomeId): number {
  const def = BIOMES[biome];
  return def.radius * Math.max(...(def.shapes ?? DISC_ONLY).map(([shape]) => standReach(shape)));
}
